/**
 * Anfrage cart guard — keeps the real Shopify cart free of anything that
 * isn't explicitly allowed.
 *
 * Store-wide "Preis auf Anfrage": every product except a small, explicit
 * set of real-priced products (plus the gift card) is request-to-buy
 * only. The theme removes the add-to-cart / buy-now UI for the €0,00
 * products, but they stay real, technically-purchasable Shopify products.
 * Without a Shopify Function (Plus-only) there is no server-side block —
 * so this script enforces it client-side:
 *
 *   - on page load / bfcache restore
 *   - right after any /cart/add call (fetch is wrapped)
 *   - and, as a last line, when a checkout trigger is clicked
 *
 * RE-ENABLED 2026-09-22 (real bug found during an account-page re-check):
 * this file existed on disk but was never loaded from layout/theme.liquid
 * — the store had moved to "real prices for everyone" on 2026-09-18, so
 * disabling it made sense at the time. It went back to "most products are
 * €0,00/Anfrage-only, only 5 named products + the gift card are real"
 * later the same week, but nobody re-enabled this guard — meaning the
 * native Shopify hosted account page's own "Erneut kaufen" button (a
 * plain /cart/<variant_id>:<qty> permalink Shopify itself renders, not
 * something this theme's files control) could add a now-€0,00 product to
 * a real cart for free from a past order. Confirmed live: clicking it for
 * an order containing papier-toilettenpapier-3lagig-250blatt-c (now
 * €0,00) did exactly that. blocks/hygeon-header-main.liquid's product
 * page CTA and sections/hygeon-account-hub.liquid's own "Erneut
 * bestellen" link were both already guarded against this at their own
 * source (the account-hub one fixed the same day, right before this) —
 * but Shopify's own hosted account page is a permalink this theme cannot
 * edit or intercept client-side before the click navigates. Only a
 * cart-level guard closes that specific gap, which is exactly what this
 * file already does — it just needed to be loaded again.
 *
 * isAllowed() also changed: it used to check only for the gift card /
 * B2B-approved flag, which is now wrong for this real state — a handful
 * of OTHER real products (the 5 named ones) are legitimately purchasable
 * too, and hardcoding their handles here would silently go stale the
 * next time that list changes in Admin. Checking the line item's own
 * real, live `price` (a genuine `/cart.js` field, in cents) instead is
 * self-maintaining: anything with a real price > 0 is allowed, anything
 * still sitting at €0,00 gets removed, with zero hardcoded product list
 * to keep in sync.
 *
 * Three things are let through (see isAllowed()): any line item with a
 * real price above zero (covers the gift card and the 5 named products
 * automatically), an approved B2B customer (window.HygeonB2BApproved),
 * and — 2026-09-13 — any line added via the €0 B2B-checkout "Unverbindlich
 * bestellen" button, marked with a hidden `_b2b_bestellung: "ja"`
 * line-item property. This is the deliberate, opt-in SECOND
 * request-to-buy path that runs alongside the default Anfrageliste
 * (contact-form, no-cart) path — see blocks/hygeon-anfrage-cta.liquid
 * for both buttons.
 *
 * Everything else is set to quantity 0, a short German notice is shown,
 * and the theme's cart UI is refreshed.
 */
(function () {
  'use strict';

  var NOTICE_ID = 'hygeon-anfrage-cart-notice';
  var busy = false;

  function isAllowed(item) {
    // 2026-09-11: an approved B2B customer (window.HygeonB2BApproved, set
    // server-side in layout/theme.liquid from the real "b2b-approved"
    // customer tag) can add ANY real product — this guard's whole job is
    // to keep non-approved carts request-to-buy-only, not to block a
    // genuine direct-order customer.
    if (window.HygeonB2BApproved) return true;
    // A real, live price above zero (Shopify's own /cart.js `price`
    // field, in cents) — covers the gift card and every currently
    // real-priced product without hardcoding which ones those are.
    if (item && typeof item.price === 'number' && item.price > 0) return true;
    // 2026-09-13: the €0 B2B-checkout flow (blocks/hygeon-anfrage-cta.liquid's
    // "Unverbindlich bestellen" button) adds with a hidden line-item
    // property `_b2b_bestellung: "ja"` — Shopify hides any property key
    // starting with "_" from the customer-facing cart/checkout UI, so this
    // never leaks as visible line-item text. Any item carrying it is a
    // deliberate opt-in checkout-as-a-request, distinct from the default
    // Anfrageliste (no-cart, contact-form) path — let it through.
    if (item && item.properties && item.properties._b2b_bestellung === 'ja') return true;
    return false;
  }

  function showNotice() {
    if (document.getElementById(NOTICE_ID)) return;
    var n = document.createElement('div');
    n.id = NOTICE_ID;
    n.setAttribute('role', 'alert');
    n.textContent =
      'Dieser Artikel ist ausschließlich auf Anfrage erhältlich und wurde aus dem Warenkorb entfernt. Bitte nutzen Sie die Anfrageliste.';
    n.style.cssText = [
      'position:fixed',
      'left:50%',
      'bottom:24px',
      'transform:translateX(-50%)',
      'z-index:2147483000',
      'max-width:520px',
      'width:calc(100% - 32px)',
      'box-sizing:border-box',
      'padding:14px 18px',
      'background:#ffffff',
      'color:#333E48',
      'border:2px solid #1565C0',
      'border-radius:12px',
      'box-shadow:0 8px 28px rgba(0,0,0,0.18)',
      "font-family:'Inter',sans-serif",
      'font-size:14px',
      'line-height:1.45',
      'text-align:center'
    ].join(';');
    (document.body || document.documentElement).appendChild(n);
    setTimeout(function () {
      if (n && n.parentNode) n.parentNode.removeChild(n);
    }, 9000);
  }

  function refreshCartUi() {
    // Nudge whatever cart UI is on the page to re-read /cart.
    try { document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); } catch (e) {}
    try { document.dispatchEvent(new CustomEvent('shopify:cart:lines-update', { bubbles: true })); } catch (e) {}
    try { document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true })); } catch (e) {}
  }

  /**
   * @param {boolean} silent  don't show the notice (used for the quiet
   *        load-time sweep so a returning visitor isn't nagged).
   * @returns {Promise<boolean>} true if something was removed.
   */
  function cleanCart(silent) {
    if (busy) return Promise.resolve(false);
    busy = true;
    // 2026-10-03: shared, deduped cart read — see
    // assets/hygeon-shared-cart-fetch.js's own header comment for why.
    return window.HygeonCartFetch()
      .then(function (cart) {
        var updates = {};
        var removed = false;
        (cart.items || []).forEach(function (it) {
          if (!isAllowed(it)) {
            updates[it.key] = 0;
            removed = true;
          }
        });
        if (!removed) { busy = false; return false; }
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates })
        }).then(function () {
          busy = false;
          if (!silent) showNotice();
          refreshCartUi();
          return true;
        });
      })
      .catch(function () { busy = false; return false; });
  }

  // 1. Quiet sweep on load + bfcache restore.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { cleanCart(true); });
  } else {
    cleanCart(true);
  }
  window.addEventListener('pageshow', function (e) { if (e.persisted) cleanCart(true); });

  // 2. Wrap fetch so any add-to-cart is followed by a sweep.
  if (window.fetch && !window.fetch.__hygeonAnfrageWrapped) {
    var origFetch = window.fetch;
    var wrapped = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var p = origFetch.apply(this, arguments);
      if (/\/cart\/add(\.js)?(\?|$|#)/.test(url)) {
        p.then(function () { setTimeout(function () { cleanCart(false); }, 60); }).catch(function () {});
      }
      return p;
    };
    wrapped.__hygeonAnfrageWrapped = true;
    window.fetch = wrapped;
  }

  // Also catch old-style XHR add-to-cart, just in case a script uses it.
  if (window.XMLHttpRequest && !window.XMLHttpRequest.prototype.__hygeonAnfragePatched) {
    var origOpen = window.XMLHttpRequest.prototype.open;
    window.XMLHttpRequest.prototype.open = function (method, url) {
      this.__hygeonAnfrageIsAdd = /\/cart\/add(\.js)?(\?|$|#)/.test(String(url || ''));
      return origOpen.apply(this, arguments);
    };
    var origSend = window.XMLHttpRequest.prototype.send;
    window.XMLHttpRequest.prototype.send = function () {
      if (this.__hygeonAnfrageIsAdd) {
        this.addEventListener('load', function () { setTimeout(function () { cleanCart(false); }, 60); });
      }
      return origSend.apply(this, arguments);
    };
    window.XMLHttpRequest.prototype.__hygeonAnfragePatched = true;
  }

  // 3. Last line: any click on a checkout trigger sweeps first.
  document.addEventListener(
    'click',
    function (e) {
      if (!e.target || !e.target.closest) return;
      var trigger = e.target.closest(
        'button[name="checkout"], input[name="checkout"], [data-hygeon-anfrage-cart-guard], a[href$="/checkout"], a[href*="/checkout?"], a[href*="/checkout#"]'
      );
      if (!trigger) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      cleanCart(false).then(function (removed) {
        if (removed) return; // stayed put; notice shown
        window.location.href = '/checkout';
      });
    },
    true
  );
})();
